import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { getCreatedToastMessage, getErrorToastMessage } from '@/libs/api'
import { QUERY_KEYS, STALE_TIME } from '@/libs/constants'
import type { HookParams, LocalPermissions, LocalPermissionsListParams } from '@/libs/interfaces'
import { permissionsApi } from '@/services/api/permissions'

export function usePermissions({ params, enabled }: HookParams<LocalPermissionsListParams>) {
  return useQuery<Partial<LocalPermissions[]>>({
    queryKey: QUERY_KEYS.permissions.list(params),
    queryFn: () => permissionsApi.list(params),
    enabled: enabled ?? true,
    staleTime: STALE_TIME.Instant,
  })
}

export function useAllPermissions({ params, enabled }: HookParams<LocalPermissionsListParams>) {
  return useQuery<LocalPermissions[]>({
    queryKey: [...QUERY_KEYS.permissions.all, 'allPages', params.orgID],
    queryFn: () => permissionsApi.listAll(params),
    enabled: enabled ?? true,
    staleTime: STALE_TIME.Instant,
  })
}

// Role→permission mapping responses embed full permission objects
// (permDispName/description), so permission-definition edits must also
// invalidate roles.all to keep cached role mappings (and the live ability) fresh.
export function usePermissionsCreate() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: permissionsApi.create,
    onSuccess: (result) => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.permissions.all,
      })
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.roles.all,
      })
      toast.success(getCreatedToastMessage(result, t('roles.permissionCreated')))
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('common.error')))
    },
  })
}

export function usePermissionsDelete() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: permissionsApi.delete,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.permissions.all,
      })
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.roles.all,
      })
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('common.error')))
    },
  })
}

export function usePermissionsUpdate() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: permissionsApi.update,
    onSuccess: (_data) => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.permissions.all,
      })
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.roles.all,
      })
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('common.error')))
    },
  })
}
